import { useCallback, useMemo } from 'react';
import { http } from '@frontend/fetch';
import { shouldLazyLoad } from '../constants';
import { DEFAULT_PREFERENCES, type DefaultPreferencesType } from '../DEFAULTS';
import { preferenceActions, usePreferenceStore } from '../store';
import { PreferenceDict, PreferenceKey, PreferenceState, StoredPreference } from '../types';
import { usePreferenceMutation, useLazyPreferenceQuery } from './queries';

function useLazyLoadingState<K extends PreferenceKey>(id: K) {
  const shouldBeLazyLoaded = useMemo(() => shouldLazyLoad(id), [id]);

  const isLazyLoaded = useMemo(
    () => (shouldBeLazyLoaded ? preferenceActions.isLazyLoaded(id) : true),
    [id, shouldBeLazyLoaded]
  );

  const shouldTriggerLazyLoad = shouldBeLazyLoaded && !isLazyLoaded;

  return {
    shouldBeLazyLoaded,
    isLazyLoaded,
    shouldTriggerLazyLoad,
  };
}

function usePreferenceStoreSubscription<K extends PreferenceKey, T>(
  id: K,
  selector: ((value: any) => T) | undefined,
  actualOmitDefaultValue: boolean
) {
  const getRawValue = useCallback(
    (prefData: StoredPreference<any>) => {
      return (
        prefData?.value ?? (!actualOmitDefaultValue ? DEFAULT_PREFERENCES[id as DefaultPreferencesType] : undefined)
      );
    },
    [id, actualOmitDefaultValue]
  );

  const storeSelector = useCallback(
    (state: PreferenceState) => {
      const prefData = state.prefs[id];
      const rawValue = getRawValue(prefData);

      return {
        value: selector ? selector(rawValue) : rawValue,
        isDataLoaded: !!prefData,
        updatedAt: prefData?.updatedAt,
      };
    },
    [id, getRawValue, selector]
  );

  const equalityFn = useCallback(
    (prev: any, next: any) => {
      if (selector) {
        return (
          prev.value === next.value && prev.isDataLoaded === next.isDataLoaded && prev.updatedAt === next.updatedAt
        );
      }

      return prev.isDataLoaded === next.isDataLoaded && prev.updatedAt === next.updatedAt;
    },
    [selector]
  );

  return usePreferenceStore(storeSelector, equalityFn);
}

function usePreferenceSetter<K extends PreferenceKey>(id: K) {
  const { mutate } = usePreferenceMutation();

  return useCallback(
    (newValue: PreferenceDict[K]) => {
      const updatedAt = new Date().toISOString();

      preferenceActions.setPreference(id, newValue, updatedAt);

      mutate({
        id,
        value: newValue,
        updatedAt,
      });
    },
    [id, mutate]
  );
}

/**
 * Hook for managing user preferences, that persists and syncs across devices.
 *
 * @example
 * // Basic usage - subscribes to entire preference value
 * const [filters, setFilters] = usePreference('ForYouNotificationFilters');
 *
 * @example
 * // With selector - subscribes only to the length of the array
 * const [filterCount, setFilters] = usePreference(
 *   'ForYouNotificationFilters',
 *   { selector: (filters) => filters?.length ?? 0 }
 * );
 *
 * @example
 * // With selector and omitDefaultValue
 * const [firstWidget, setWidgets] = usePreference(
 *   'WidgetConfigs',
 *   {
 *     selector: (widgets) => widgets?.[0],
 *     omitDefaultValue: true
 *   }
 * );
 */

type UsePreferenceOptions<K extends PreferenceKey, T = never, D extends boolean = false> = {
  selector?: (
    value: K extends DefaultPreferencesType
      ? D extends true
        ? PreferenceDict[K] | undefined
        : PreferenceDict[K]
      : PreferenceDict[K] | undefined
  ) => T;
  omitDefaultValue?: D;
  enabled?: boolean;
};

export function usePreference<K extends PreferenceKey, T = never, D extends boolean = false>(
  id: K,
  options?: UsePreferenceOptions<K, T, D>
): [
  [T] extends [never]
    ? K extends DefaultPreferencesType
      ? D extends true
        ? PreferenceDict[K] | undefined
        : PreferenceDict[K]
      : PreferenceDict[K] | undefined
    : T,
  (value: PreferenceDict[K]) => void,
  { isLoading?: boolean }
] {
  options = options || {};
  const { selector, omitDefaultValue = false, enabled = true } = options;

  const { shouldBeLazyLoaded, shouldTriggerLazyLoad } = useLazyLoadingState(id);

  const { value, isDataLoaded } = usePreferenceStoreSubscription(id, selector, omitDefaultValue);

  const setPreference = usePreferenceSetter(id);

  const lazyQuery = useLazyPreferenceQuery(id, shouldTriggerLazyLoad && enabled);

  const isLoading = shouldBeLazyLoaded && !isDataLoaded && lazyQuery.isLoading;

  return [value, setPreference, { isLoading }];
}

// Helper function to update preference value outside of React components
export function updatePreferenceValue<K extends PreferenceKey>(
  id: K,
  value: PreferenceDict[K],
  optionalUpdatedAt?: string
): void {
  const updatedAt = optionalUpdatedAt ?? new Date().toISOString();
  // Update Zustand store
  preferenceActions.setPreference(id, value, updatedAt);

  const payload = {
    userSettingsMap: {
      [id]: JSON.stringify(value),
    },
    updatedAt: updatedAt,
    clientId: usePreferenceStore.getState().clientId,
  };

  http.patch('user-preferences/settings', payload);
}
